<template>
  <div class="mb-4 rounded-lg border border-gray-200 bg-white p-3 shadow-xs">
    <div class="flex items-center gap-3">
      <ol class="flex flex-1 items-center pb-1 min-w-0">
        <template v-for="(item, idx) in stepperItems" :key="item.key">
          <li class="flex items-center">
            <button
              type="button"
              class="group flex items-center text-left"
              :disabled="!item.canNavigate"
              :aria-current="item.key === displayActiveStepKey ? 'step' : undefined"
              @click="item.canNavigate && $emit('goToStepKey', item.key)"
            >
              <span
                class="flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold transition-colors"
                :class="stepperCircleClass(item)"
              >
                {{ stepperCircleText(item) }}
              </span>
              <span class="ml-2 text-xs font-semibold truncate" :class="stepperLabelClass(item)">{{ item.label }}</span>
            </button>
          </li>
          <div
            v-if="idx < stepperItems.length - 1"
            class="mx-2 h-0.5 flex-1 rounded-sm"
            :class="stepperConnectorClass(idx)"
          />
        </template>
      </ol>
      <button
        type="button"
        class="cursor-pointer p-1 rounded-sm text-gray-800 hover:text-primary-dark hover:bg-gray-100 transition-colors"
        title="Réinitialiser le formulaire"
        aria-label="Réinitialiser le formulaire"
        @click="$emit('reset')"
      >
        <span class="text-base leading-none" aria-hidden="true">↺</span>
      </button>
    </div>
  </div>
</template>

<script>
const STEP_LABELS = [
  { key: "seller", label: "Vendeur" },
  { key: "advisory", label: "Conseil" },
  { key: "product", label: "Produit" },
  { key: "eligibility", label: "Éligibilité" },
  { key: "customer", label: "Client" },
  { key: "actions", label: "Récapitulatif" },
];
const STEP_ORDER = Object.fromEntries(STEP_LABELS.map((s, i) => [s.key, i + 1]));

export default {
  name: "SubscriptionProgressBar",
  props: {
    stepKeys: {
      type: Array,
      required: true,
    },
    activeStepIndex: {
      type: Number,
      required: true,
    },
    maxReachableStepIndex: {
      type: Number,
      required: true,
    },
    category: {
      type: [Object, String],
      default: null,
    },
    productless: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["goToStepKey", "reset"],
  computed: {
    displayActiveStepKey() {
      return this.stepKeys[this.activeStepIndex] || "seller";
    },
    stepperItems() {
      return STEP_LABELS.reduce((items, item) => {
        const indexInFlow = this.stepKeys.indexOf(item.key);
        const isSkipped = item.key === "product" && this.category && this.productless;
        if (indexInFlow === -1 && !isSkipped) return items;

        const status = isSkipped
          ? "skipped"
          : item.key === this.displayActiveStepKey
            ? "current"
            : indexInFlow < this.activeStepIndex
              ? "complete"
              : "upcoming";

        items.push({
          ...item,
          indexInFlow,
          status,
          canNavigate: indexInFlow !== -1 && indexInFlow <= this.maxReachableStepIndex,
        });
        return items;
      }, []);
    },
  },
  methods: {
    stepperCircleText(item) {
      if (item.status === "complete") return "✓";
      if (item.status === "skipped") return "–";
      return STEP_ORDER[item.key] || "";
    },

    stepperCircleClass(item) {
      switch (item.status) {
        case "complete":
          return "border-gray-900 bg-gray-900 text-white";
        case "current":
          return "border-gray-900 bg-white text-gray-900";
        case "skipped":
          return "border-gray-300 bg-gray-100 text-gray-600";
        case "locked":
          return "border-gray-200 bg-gray-50 text-gray-400";
        default:
          return "border-gray-300 bg-white text-gray-700";
      }
    },

    stepperLabelClass(item) {
      switch (item.status) {
        case "complete":
          return "text-gray-900";
        case "current":
          return "text-gray-900";
        case "locked":
          return "text-gray-400";
        default:
          return "text-gray-700";
      }
    },

    stepperConnectorClass(idx) {
      const left = this.stepperItems[idx];
      const right = this.stepperItems[idx + 1];
      const active = (left.status === "complete" || left.status === "skipped") && right.status !== "locked";
      return active ? "bg-gray-900" : "bg-gray-200";
    },
  },
};
</script>
